import { isCatalogImportCode } from '@/api/handlers/resources/catalogImports';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { CatalogImportResult } from '@/api/models';
import {
    noticeFor,
    type CatalogImportNotice,
} from '@/pages/catalogImport/notices';
import type { NamesDecisions } from '@/pages/catalogImport/store';
import {
    NAME_KINDS,
    reviewOf,
    unreviewedAxesCount,
    type ShownAxes,
} from './namesDecisions';

/**
 * Where Check products stands. Pure, so every outcome is tested without the
 * API: the step goes on to Products only when the check answers with nothing
 * to settle here.
 */
export type NamesCheck =
    | { status: 'idle' }
    | { status: 'checking' }
    /** The check did not answer; pressing Check products again retries. */
    | { status: 'failed' }
    /** The header's problems, or the request's refusal, one sentence each. */
    | { status: 'blocked'; notices: CatalogImportNotice[] }
    /** Names the decisions could not resolve; each row says why. */
    | { status: 'namesNeedAttention'; count: number }
    /**
     * Categories the answer adds axes to that their rows did not show when
     * the decisions were sent; the rows show them now, with the checkbox.
     */
    | { status: 'axesToReview'; count: number };

export const NAMES_CHECK_IDLE: NamesCheck = { status: 'idle' };

/**
 * What a check's answer means for the step: `null` to go on to Products, or
 * why to stay. A header problem blocks the whole file; a name the decisions
 * left unresolved (an ambiguity, a target that no longer exists, a merge
 * loop) is settled here, not in the table; and axes the answer would add to
 * a category whose row did not show them when the decisions were sent
 * (`shown`, `unreviewedAxesCount`) are looked at here before the import can
 * change that category.
 */
export const namesCheckOfPreview = (
    preview: CatalogImportResult,
    decisions: NamesDecisions,
    shown: ShownAxes,
): NamesCheck | null => {
    if (preview.header.problems.length > 0) {
        return {
            status: 'blocked',
            notices: preview.header.problems.map(noticeFor),
        };
    }
    const { entries } = reviewOf(preview.names, decisions);
    const count = NAME_KINDS.reduce(
        (sum, kind) =>
            sum +
            entries[kind].filter((entry) => entry.problems.length > 0).length,
        0,
    );
    if (count > 0) {
        return { status: 'namesNeedAttention', count };
    }
    const axes = unreviewedAxesCount(entries.categories, shown);
    return axes > 0 ? { status: 'axesToReview', count: axes } : null;
};

/**
 * A refused check: a code of the catalog import has its sentence; anything
 * else (no answer, a code the import does not use) is worth a retry.
 */
export const namesCheckOfError = (error: ErrorResponse | null): NamesCheck =>
    error?.code !== undefined && isCatalogImportCode(error.code)
        ? { status: 'blocked', notices: [noticeFor({ code: error.code })] }
        : { status: 'failed' };
